import { useState } from "react";
import DOMPurify from "dompurify";
import axios from "axios";
import { toast } from "sonner";
import { Pin, Check } from "lucide-react";
import { API } from "@/context/AuthContext";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

const COLORS = ["#0369a1", "#7c3aed", "#059669", "#d97706", "#dc2626", "#0891b2", "#4f46e5", "#65a30d", "#db2777", "#78716c", "#2563eb", "#ca8a04"];

const fmt = (v) => {
  if (typeof v !== "number") return v;
  const a = Math.abs(v);
  if (a >= 1e7) return `${(v / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `${(v / 1e5).toFixed(2)} L`;
  return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

export function renderMarkdown(text) {
  const esc = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = esc.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const l = lines[i];
    if (/^\s*\|.*\|\s*$/.test(l) && i + 1 < lines.length && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      const cells = (r) => r.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      const head = cells(l);
      i += 2;
      const body = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { body.push(cells(lines[i])); i++; }
      out.push(`<table><thead><tr>${head.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>${body.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`).join("")}</tbody></table>`);
      continue;
    }
    out.push(l);
    i++;
  }
  let html = out.join("\n")
    .replace(/^### (.*)$/gm, "<h3>$1</h3>").replace(/^## (.*)$/gm, "<h2>$1</h2>").replace(/^# (.*)$/gm, "<h1>$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/^\s*[-*] (.*)$/gm, "<li>$1</li>").replace(/(<li>[\s\S]*?<\/li>)(?!\s*<li>)/g, "<ul>$1</ul>")
    .replace(/^\s*\d+\. (.*)$/gm, "<li>$1</li>")
    .replace(/\n{2,}/g, "<br/>");
  const clean = DOMPurify.sanitize(html, { ALLOWED_TAGS: ["h1", "h2", "h3", "strong", "em", "li", "ul", "ol", "br", "code", "table", "thead", "tbody", "tr", "th", "td"], ALLOWED_ATTR: [] });
  return { __html: clean };
}

// Markdown tables become their own segments so they can be pinned individually.
const TABLE_RE = /(^|\n)((?:[ \t]*\|.*\|[ \t]*\n)[ \t]*\|[\s:|-]+\|[ \t]*(?:\n[ \t]*\|.*\|[ \t]*)*)/g;
function splitTables(text) {
  const out = [];
  let last = 0, m;
  while ((m = TABLE_RE.exec(text))) {
    const start = m.index + m[1].length;
    if (start > last) out.push({ kind: "md", text: text.slice(last, start) });
    out.push({ kind: "table", text: m[2].trim() });
    last = start + m[2].length;
  }
  if (last < text.length) out.push({ kind: "md", text: text.slice(last) });
  return out;
}

// Split an answer into text + chart + table segments ("```chart {json} ```"); an unterminated block (still streaming) is hidden.
export function splitSegments(text) {
  const segs = [];
  const re = /```chart\s*([\s\S]*?)```/g;
  let last = 0, m;
  while ((m = re.exec(text))) {
    if (m.index > last) segs.push(...splitTables(text.slice(last, m.index)));
    try { segs.push({ kind: "chart", spec: JSON.parse(m[1]), raw: m[1].trim() }); } catch { segs.push({ kind: "md", text: "" }); }
    last = m.index + m[0].length;
  }
  const rest = text.slice(last);
  const open = rest.indexOf("```chart");
  segs.push(...splitTables(open >= 0 ? rest.slice(0, open) : rest));
  return segs;
}

export function PinButton({ kind, content, title, question, sources, testId }) {
  const [state, setState] = useState("idle");
  if (!question) return null;
  const pin = async () => {
    setState("busy");
    try {
      await axios.post(`${API}/ai/pins`, { title: (title || question).slice(0, 120), question, kind, sources: sources || [], content });
      setState("done");
      toast.success("Pinned to your Dashboard");
    } catch (e) {
      setState("idle");
      toast.error(e.response?.data?.detail || "Could not pin");
    }
  };
  return (
    <button type="button" onClick={pin} disabled={state !== "idle"} data-testid={testId} title="Pin to dashboard (refreshes with live data)"
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${state === "done" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500 hover:border-sky-300 hover:text-sky-700"}`}>
      {state === "done" ? <><Check className="h-3 w-3" />Pinned</> : <><Pin className="h-3 w-3" />{state === "busy" ? "Pinning…" : "Pin to dashboard"}</>}
    </button>
  );
}

export function AiChart({ spec, idx, pin }) {
  if (!spec || !Array.isArray(spec.data) || spec.data.length === 0) return null;
  const xKey = spec.xKey || "name";
  const series = Array.isArray(spec.series) && spec.series.length ? spec.series : [{ key: Object.keys(spec.data[0]).find((k) => k !== xKey) || "value", label: "Value" }];
  const type = spec.type || "bar";
  return (
    <div className="my-3 rounded-md border border-slate-200 bg-white p-3" data-testid={`ai-chart-${idx}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        {spec.title ? <p className="text-xs font-semibold text-slate-700">{spec.title}</p> : <span />}
        {pin}
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          {type === "pie" ? (
            <PieChart>
              <Pie data={spec.data} dataKey={series[0].key} nameKey={xKey} outerRadius={80} label={({ name }) => String(name).slice(0, 16)} labelLine={false}>
                {spec.data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: 11 }} />
            </PieChart>
          ) : type === "line" ? (
            <LineChart data={spec.data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey={xKey} tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
              <YAxis tickFormatter={fmt} tick={{ fontSize: 10 }} width={60} />
              <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: 11 }} />
              {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
              {series.map((s, i) => <Line key={s.key} type="monotone" dataKey={s.key} name={s.label || s.key} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />)}
            </LineChart>
          ) : (
            <BarChart data={spec.data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey={xKey} tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} tickFormatter={(v) => String(v).slice(0, 14)} />
              <YAxis tickFormatter={fmt} tick={{ fontSize: 10 }} width={60} />
              <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: 11 }} />
              {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
              {series.map((s, i) => (
                <Bar key={s.key} dataKey={s.key} name={s.label || s.key} radius={[3, 3, 0, 0]} fill={COLORS[i % COLORS.length]}>
                  {series.length === 1 && spec.data.map((d, j) => <Cell key={j} fill={typeof d[s.key] === "number" && d[s.key] < 0 ? "#dc2626" : COLORS[i % COLORS.length]} />)}
                </Bar>
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function AiAnswer({ text, sources, status, question, streaming }) {
  const segs = splitSegments(text || "");
  const canPin = !!question && !streaming;
  return (
    <div className="ai-markdown">
      {segs.map((s, i) => {
        if (s.kind === "chart") {
          return <AiChart key={i} spec={s.spec} idx={i} pin={canPin && <PinButton kind="chart" content={s.raw} title={s.spec.title} question={question} sources={sources} testId={`ai-pin-chart-${i}`} />} />;
        }
        if (s.kind === "table") {
          return (
            <div key={i} className="my-2" data-testid={`ai-table-${i}`}>
              <div dangerouslySetInnerHTML={renderMarkdown(s.text)} />
              {canPin && <div className="mt-1 flex justify-end"><PinButton kind="table" content={s.text} title={question} question={question} sources={sources} testId={`ai-pin-table-${i}`} /></div>}
            </div>
          );
        }
        return s.text ? <div key={i} dangerouslySetInnerHTML={renderMarkdown(s.text)} /> : null;
      })}
      {!text && status && <p className="text-xs text-slate-500 italic" data-testid="ai-status">{status}</p>}
      {sources?.length > 0 && text && (
        <p className="mt-2 pt-2 border-t border-slate-200 text-[11px] text-slate-500" data-testid="ai-sources">
          Data used: {sources.map((s) => `${s.dataset}${Object.keys(s.params || {}).length ? ` (${Object.entries(s.params).map(([k, v]) => `${k}=${v}`).join(", ")})` : ""}`).join(" · ")}
        </p>
      )}
    </div>
  );
}
